import useGetHouseholdAccounts from "@fartherfinance/frontend/api/Households/hooks/useGetHouseholdAccounts";
import {
  HouseholdAccount,
  HouseholdId,
} from "@fartherfinance/frontend/api/Households/types";
import { PaginationMetadata } from "@fartherfinance/frontend/api/Types";

const PAGE_SIZE = 5;

interface AccountsWithPagination {
  data: HouseholdAccount[];
  pagination: PaginationMetadata | undefined;
}

interface UseGetHouseholdAccountsFilterResult {
  fartherAccounts: AccountsWithPagination;
  manualAccounts: AccountsWithPagination;
  creditAccounts: AccountsWithPagination;
  depositoryAccounts: AccountsWithPagination;
  investmentAccounts: AccountsWithPagination;
  loanAccounts: AccountsWithPagination;
  savingsAccounts: AccountsWithPagination;
  checkingAccounts: AccountsWithPagination;
  otherExternalAccounts: AccountsWithPagination;
  isLoading: boolean;
}

export const useGetHouseholdAccountsFilter = (
  householdId: HouseholdId,
  paginationState: Record<string, number>,
  auth: any
): UseGetHouseholdAccountsFilterResult => {
  const { data: fartherAccountsData, isLoading: isLoadingFarther } =
    useGetHouseholdAccounts(
      householdId,
      {
        page: paginationState["FartherManagedAccount"] || 1,
        pageSize: PAGE_SIZE,
        accountType: "FartherManagedAccount",
      },
      auth
    );

  const { data: manualAccountsData, isLoading: isLoadingManual } =
    useGetHouseholdAccounts(
      householdId,
      {
        page: paginationState["ManuallyTrackedAccount"] || 1,
        pageSize: PAGE_SIZE,
        accountType: "ManuallyTrackedAccount",
      },
      auth
    );

  const { data: creditAccountsData, isLoading: isLoadingCredit } =
    useGetHouseholdAccounts(
      householdId,
      {
        page: paginationState["credit"] || 1,
        pageSize: PAGE_SIZE,
        accountType: "credit",
      },
      auth
    );

  const { data: depositoryAccountsData, isLoading: isLoadingDepository } =
    useGetHouseholdAccounts(
      householdId,
      {
        page: paginationState["depository"] || 1,
        pageSize: PAGE_SIZE,
        accountType: "depository",
      },
      auth
    );

  const { data: investmentAccountsData, isLoading: isLoadingInvestment } =
    useGetHouseholdAccounts(
      householdId,
      {
        page: paginationState["investment"] || 1,
        pageSize: PAGE_SIZE,
        accountType: "investment",
      },
      auth
    );

  const { data: loanAccountsData, isLoading: isLoadingLoan } =
    useGetHouseholdAccounts(
      householdId,
      {
        page: paginationState["loan"] || 1,
        pageSize: PAGE_SIZE,
        accountType: "loan",
      },
      auth
    );

  const { data: savingsAccountsData, isLoading: isLoadingSavings } =
    useGetHouseholdAccounts(
      householdId,
      {
        page: paginationState["Savings"] || 1,
        pageSize: PAGE_SIZE,
        accountType: "Savings",
      },
      auth
    );

  const { data: checkingAccountsData, isLoading: isLoadingChecking } =
    useGetHouseholdAccounts(
      householdId,
      {
        page: paginationState["Checking"] || 1,
        pageSize: PAGE_SIZE,
        accountType: "Checking",
      },
      auth
    );

  const fartherAccounts: AccountsWithPagination = {
    data: fartherAccountsData?.data ?? [],
    pagination: fartherAccountsData?.pagination,
  };

  const manualAccounts: AccountsWithPagination = {
    data: manualAccountsData?.data ?? [],
    pagination: manualAccountsData?.pagination,
  };

  const creditAccounts: AccountsWithPagination = {
    data: creditAccountsData?.data ?? [],
    pagination: creditAccountsData?.pagination,
  };

  const depositoryAccounts: AccountsWithPagination = {
    data: depositoryAccountsData?.data ?? [],
    pagination: depositoryAccountsData?.pagination,
  };

  const investmentAccounts: AccountsWithPagination = {
    data: investmentAccountsData?.data ?? [],
    pagination: investmentAccountsData?.pagination,
  };

  const loanAccounts: AccountsWithPagination = {
    data: loanAccountsData?.data ?? [],
    pagination: loanAccountsData?.pagination,
  };

  const savingsAccounts: AccountsWithPagination = {
    data: savingsAccountsData?.data ?? [],
    pagination: savingsAccountsData?.pagination,
  };

  const checkingAccounts: AccountsWithPagination = {
    data: checkingAccountsData?.data ?? [],
    pagination: checkingAccountsData?.pagination,
  };

  const otherExternalAccounts: AccountsWithPagination = {
    data: [],
    pagination: undefined,
  };

  const isLoading =
    isLoadingFarther ||
    isLoadingManual ||
    isLoadingCredit ||
    isLoadingDepository ||
    isLoadingInvestment ||
    isLoadingLoan ||
    isLoadingSavings ||
    isLoadingChecking;

  return {
    fartherAccounts,
    manualAccounts,
    creditAccounts,
    depositoryAccounts,
    investmentAccounts,
    loanAccounts,
    savingsAccounts,
    checkingAccounts,
    otherExternalAccounts,
    isLoading,
  };
};
